/**
 * usePresenceTracker Hook
 *
 * Sends lightweight heartbeat signals to the Flask backend for session tracking.
 * Uses session ID authentication instead of Firebase tokens to minimize CPU overhead.
 *
 * Features:
 * - Heartbeat every 60 seconds
 * - Immediate heartbeat on page navigation
 * - Logout notification via navigator.sendBeacon
 * - Includes current page, user agent, and selected location
 * - Auto-recovery: If session is lost (e.g., server restart), sends Firebase token
 *   to recreate the session transparently
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useFirebaseStore } from '../store/firebaseStore';
import { useTelemetryStore } from '../store/telemetryStore';
import { getInitialBuildId, getCachedVersion } from '../utils/versionState';
import { logger } from '../utils/logger';

const HEARTBEAT_INTERVAL_MS = 60000; // 60 seconds
const IDLE_THRESHOLD_MS = 60000; // No input for 60s (or hidden tab) => idle

/**
 * Sends heartbeat to track user presence and current page
 */
export function usePresenceTracker() {
  const sessionId = useFirebaseStore((state) => state.sessionId);
  const setSessionId = useFirebaseStore((state) => state.setSessionId);
  const isConnected = useFirebaseStore((state) => state.isConnected);
  const selectedLocation = useFirebaseStore((state) => state.selectedLocation);
  const getAndClearTelemetry = useTelemetryStore((state) => state.getAndClearTelemetry);
  const getAndClearRtdbTelemetry = useTelemetryStore((state) => state.getAndClearRtdbTelemetry);
  const recordNavEvent = useTelemetryStore((state) => state.recordNavEvent);
  const getAndClearErrors = useTelemetryStore((state) => state.getAndClearErrors);
  const getAndClearNavEvents = useTelemetryStore((state) => state.getAndClearNavEvents);
  const location = useLocation();
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastHeartbeatRef = useRef<number>(0);
  // Last user-input time (cheap ref — updated on input without re-rendering)
  const lastActivityRef = useRef<number>(Date.now());
  // Page currently being viewed + when it was entered, for dwell-time tracking
  const navRef = useRef<{ page: string; enteredAt: number } | null>(null);

  /**
   * Send a heartbeat to the server
   * Includes telemetry data (Firestore usage stats) accumulated since last heartbeat
   *
   * If the session was lost (e.g., server restart), the backend will use the
   * Firebase Authorization header to recreate the session and return a new
   * session ID, which we store for future heartbeats.
   */
  const sendHeartbeat = useCallback(async () => {
    // Need either an active session or a Firebase connection to send heartbeat.
    // When sessionId is null but Firebase is connected (e.g., session resume),
    // we'll bootstrap a new backend session using the Firebase token.
    if (!sessionId && !isConnected) {
      return;
    }

    // Debounce: Don't send more than once per 5 seconds
    const now = Date.now();
    if (now - lastHeartbeatRef.current < 5000) {
      return;
    }
    lastHeartbeatRef.current = now;

    // Get and clear telemetry data accumulated since last heartbeat
    // This returns the stats and resets counters for next interval
    const telemetry = getAndClearTelemetry();
    const rtdbTelemetry = getAndClearRtdbTelemetry();

    // Build headers - include session ID if available, Firebase token for recovery/bootstrap
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (sessionId) {
      headers['X-Session-ID'] = sessionId;
    }

    // Include Firebase token for session recovery (server restart) or bootstrap (session resume)
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const idToken = await user.getIdToken();
        headers['Authorization'] = `Bearer ${idToken}`;
      } else if (!sessionId) {
        // No session and no Firebase user — can't send heartbeat
        return;
      }
    } catch {
      if (!sessionId) {
        // Can't bootstrap a session without a Firebase token
        logger.warn('[Presence] Could not get Firebase token for session bootstrap');
        return;
      }
      // Token fetch failed but we have a sessionId — continue without it
      logger.warn('[Presence] Could not get Firebase token for heartbeat');
    }

    // Build the app version the user is actually running (best for debugging)
    // and whether they're actively using the tab vs idle/backgrounded.
    const buildId = getInitialBuildId() ?? undefined;
    const buildVersion = getCachedVersion()?.version;
    const isActive =
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible' &&
      now - lastActivityRef.current < IDLE_THRESHOLD_MS;

    try {
      const response = await fetch('/api/sessions/heartbeat', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          currentPage: location.pathname,
          userAgent: navigator.userAgent,
          location: selectedLocation,
          buildId, // build the user loaded (for tying errors/sessions to a release)
          buildVersion,
          isActive, // active vs idle/backgrounded
          navigationEvents: getAndClearNavEvents(), // page visits + dwell since last beat
          errors: getAndClearErrors(), // client-side errors captured since last beat
          telemetry, // Firestore usage stats for cost tracking
          rtdbTelemetry, // RTDB usage stats for bandwidth tracking
        }),
      });

      if (response.ok) {
        // Check if session was created/recreated
        const data = await response.json();
        if (data.newSessionId) {
          logger.log('[Presence] Session ' + (sessionId ? 're-established (previous session expired or invalidated)' : 'bootstrapped from Firebase auth'));
          setSessionId(data.newSessionId);
        }
      } else if (response.status === 401) {
        // Session expired and could not be recreated - log but don't throw
        logger.warn('[Presence] Session expired or invalid');
      }
    } catch (error) {
      // Network error - log but don't crash
      logger.error('[Presence] Heartbeat failed:', error);
    }
  }, [sessionId, isConnected, setSessionId, location.pathname, selectedLocation, getAndClearTelemetry, getAndClearRtdbTelemetry, getAndClearNavEvents, getAndClearErrors]);

  /**
   * Send logout notification when component unmounts or user logs out.
   * Uses a ref for sessionId so the callback reference stays stable —
   * prevents spurious logout beacons from effect cleanup when sessionId changes.
   */
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const sendLogout = useCallback(() => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) {
      return;
    }

    // Use sendBeacon for reliable delivery even during page unload
    // sendBeacon sends data as 'text/plain' by default
    try {
      navigator.sendBeacon('/api/sessions/logout', currentSessionId);
      logger.log('[Presence] Logout beacon sent');
    } catch (error) {
      // Fallback to fetch if sendBeacon not available
      fetch('/api/sessions/logout', {
        method: 'POST',
        headers: { 'X-Session-ID': currentSessionId },
        keepalive: true, // Allows request to complete after page unload
      }).catch(() => {
        // Ignore errors during logout
      });
    }
  }, []);

  // Set up heartbeat interval and initial heartbeat
  // Triggers on sessionId OR isConnected — whichever comes first.
  // For MSAL auth: sessionId arrives first (from /api/auth/custom-token).
  // For Firebase resume: isConnected arrives first, bootstrapping a session via Firebase token.
  useEffect(() => {
    if (!sessionId && !isConnected) {
      return;
    }

    // Reset debounce so the first heartbeat after auth state change always fires.
    // Without this, a failed pre-auth heartbeat (401) blocks the post-auth heartbeat
    // via the 5-second debounce, leaving no server session for up to 60 seconds.
    lastHeartbeatRef.current = 0;

    // Send immediate heartbeat
    sendHeartbeat();

    // Set up interval for periodic heartbeats
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [sessionId, isConnected, sendHeartbeat]);

  // Track user activity (idle detection). Updating a ref is cheap and causes no
  // re-render, so listening to high-frequency events like mousemove is fine.
  useEffect(() => {
    const markActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener('mousemove', markActivity, opts);
    window.addEventListener('keydown', markActivity, opts);
    window.addEventListener('click', markActivity, opts);
    window.addEventListener('scroll', markActivity, opts);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') markActivity();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('mousemove', markActivity);
      window.removeEventListener('keydown', markActivity);
      window.removeEventListener('click', markActivity);
      window.removeEventListener('scroll', markActivity);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Record the dwell time for the page being left, then heartbeat on route change.
  useEffect(() => {
    const now = Date.now();
    if (navRef.current && navRef.current.page !== location.pathname) {
      recordNavEvent(navRef.current.page, navRef.current.enteredAt, now - navRef.current.enteredAt);
    }
    navRef.current = { page: location.pathname, enteredAt: now };

    if (sessionId || isConnected) {
      sendHeartbeat();
    }
  }, [location.pathname, sessionId, isConnected, sendHeartbeat, recordNavEvent]);

  // Send logout notification on unmount (page close, logout, etc.)
  useEffect(() => {
    // Handle browser/tab close
    const handleBeforeUnload = () => {
      sendLogout();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also send logout when the hook unmounts (e.g., user logs out)
      sendLogout();
    };
  }, [sendLogout]);
}

export default usePresenceTracker;
